/**
 * Kess Berlin - Stella AI custom result page
 * Loaded only when the PDP Stella snippet is rendered.
 */

(function () {
  "use strict";

  window.Kess = window.Kess || {};
  if (window.Kess.Stella) return;

  /**
   * ============================================================================
   * KESS STELLA AI — Custom result page + "Your Shade" logic
   *
   * Ports the old Kess theme behaviour (ticket 318c) into the Sleek/Relaunch
   * theme. Reuses the Stella app contract verbatim:
   *   - DOM event:  `stella-analysis-finished` (fired on document)
   *   - localStorage.stellaRecommendedProductVariants  -> [{ variant_id }, ...]
   *   - localStorage.stellaAnalysesData                -> [{ first_name }, ...]
   *   - localStorage.stellaAnalysisProductUrl          -> pathname (we write it)
   *
   * After the flow finishes (or on load when stored data exists) the raw
   * <stella-analysis> flow inside the PDP modal is replaced with a custom
   * result page built from the recommended shades. Recommended swatches across
   * every PDP get a "Your Shade" label (no tooltip); shades are only auto-
   * selected on the PDP where the analysis was made.
   * ============================================================================
   */
  Kess.Stella = {
    keys: {
      recommended: "stellaRecommendedProductVariants",
      chatRecommended: "stellaChatRecommendedProductVariants",
      analysis: "stellaAnalysesData",
      analysisUrl: "stellaAnalysisProductUrl",
    },

    bound: false,
    init() {
      if (!this.bound) {
        this.bound = true;
        document.addEventListener("stella-analysis-finished", (event) => {
          this.onFinished(event);
        });
        document.addEventListener("toggle", (event) => {
          const modal = event.target?.matches?.("[data-kess-stella]") ? event.target : null;
          if (modal?.hasAttribute("open")) this.prepareStoredResult();
        });
        document.addEventListener(
          "click",
          (event) => {
            if (event.target.closest("#show-all-recommendations-link")) {
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              window.KessOpenStellaResult?.();
              return;
            }

            if (event.target.closest("[data-kess-stella-trigger]")) {
              this.prepareStoredResult();
            }
          },
          true,
        );
        document.addEventListener("stella-popup-opened", () => {
          requestAnimationFrame(() => window.KessOpenStellaResult?.());
        });
        // Re-apply "Your Shade" markers after the variant picker / sections re-render.
        const reapply = () => {
          clearTimeout(this._reapplyTimer);
          this._reapplyTimer = setTimeout(() => this.applyMarkers(), 120);
        };
        document.addEventListener("variant:changed", reapply);
        if (window.FoxTheme?.pubsub?.subscribe && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
          window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, reapply);
        }
      }

      if (this.getRecommendedIds().length) {
        this.applyMarkers();
      }
    },

    // --- storage -----------------------------------------------------------

    parse(value, fallback) {
      try {
        const parsed = JSON.parse(value);
        return parsed == null ? fallback : parsed;
      } catch (error) {
        return fallback;
      }
    },

    getStored(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },

    setStored(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {}
    },

    clearAnalysisData() {
      try {
        window.localStorage.removeItem(this.keys.recommended);
        window.localStorage.removeItem(this.keys.chatRecommended);
        window.localStorage.removeItem(this.keys.analysis);
        window.localStorage.removeItem(this.keys.analysisUrl);
      } catch (error) {}
      this.clearMarkers();
    },

    getValidAnalysisIds() {
      const data = this.parse(this.getStored(this.keys.analysis), []);
      const list = Array.isArray(data) ? data : [data];
      const set = new Set();
      list.forEach((entry) => {
        if (entry?.analysis_id) set.add(String(entry.analysis_id));
      });
      return set;
    },

    getRecommendedIds() {
      // Analysis recommendations are the main source; chat recommendations
      // (stored by the Stella app under a separate key) fill in ids the main
      // key doesn't have yet. Main-key order wins.
      //
      // The Stella app accumulates entries across analyses in the main key, so
      // entries tagged with an analysis_id that is no longer present in
      // stellaAnalysesData are stale (mirrors the app's own
      // stellaGetRecommendedVariantIds filtering). Entries without an
      // analysis_id (chat, or our raw event fallback) are always kept.
      const validAnalyses = this.getValidAnalysisIds();
      const ids = [];
      const seen = new Set();
      [this.keys.recommended, this.keys.chatRecommended].forEach((key) => {
        const list = this.parse(this.getStored(key), []);
        if (!Array.isArray(list)) return;
        list.forEach((item) => {
          if (item?.analysis_id && validAnalyses.size && !validAnalyses.has(String(item.analysis_id))) return;
          const id =
            item == null ? null : typeof item === "object" ? (item.variant_id ?? item.variantId ?? item.id) : item;
          if (id == null || id === "" || seen.has(String(id))) return;
          seen.add(String(id));
          ids.push(String(id));
        });
      });
      return ids;
    },

    getFirstName() {
      const customerFirstName = this.getModal()?.dataset.customerFirstName;
      if (customerFirstName) return customerFirstName.trim();

      const data = this.parse(this.getStored(this.keys.analysis), []);
      const first = Array.isArray(data) ? data[0] : data;
      return (first && (first.first_name || first.firstName) ? String(first.first_name || first.firstName) : "").trim();
    },

    isAnalysisPage() {
      return this.getStored(this.keys.analysisUrl) === window.location.pathname;
    },

    onFinished(event) {
      this.setStored(this.keys.analysisUrl, window.location.pathname);

      const products = event?.detail?.products;
      if (Array.isArray(products) && products.length) {
        // The Stella app merges these products into the same key with richer
        // entries ({variant_id, analysis_id, funnel, ...}) that its own getter
        // and our stale-analysis filtering rely on. Only write our raw copy
        // when ids from this event are missing, so we don't clobber that data.
        const stored = new Set(this.getRecommendedIds());
        const missing = products.some((item) => {
          const id =
            item == null ? null : typeof item === "object" ? (item.variant_id ?? item.variantId ?? item.id) : item;
          return id != null && id !== "" && !stored.has(String(id));
        });
        if (missing) this.setStored(this.keys.recommended, JSON.stringify(products));
        if (this.renderResult()) {
          if (this.isAnalysisPage()) this.applyRecommendations();
          this.applyMarkers();
          return;
        }
      }

      this.mountLoading();
      this.pollRender(0, true);
      this.applyMarkers();
    },

    // --- helpers -----------------------------------------------------------

    normalize(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    },

    getModal() {
      return document.querySelector("[data-kess-stella]");
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    duoDescriptor(button) {
      return {
        el: button,
        type: "duo",
        colorName: button.dataset.shadeTitle || button.dataset.shadeOption || "",
        option: button.dataset.shadeOption || button.dataset.shadeTitle || "",
        swatchStyle: button.dataset.shadeSwatchStyle || "",
        image: button.dataset.shadeImage || "",
      };
    },

    // --- matching ----------------------------------------------------------

    /**
     * Resolve a recommended variant id (which may be a main OR a refill/source
     * variant id) to its shade (option) name. Built from the embedded main +
     * refill variant maps, the set component variant maps, and every shade
     * button on the page. Lets us match by name across selector types.
     */
    getRefillVariantIds() {
      const ids = new Set();
      document.querySelectorAll("[data-kess-stella-refill-variants]").forEach((el) => {
        this.parse(el.textContent, []).forEach((v) => {
          if (v?.id != null && v.id !== "") ids.add(String(v.id));
        });
      });
      return ids;
    },

    prioritizeRefillIds(ids) {
      const refillIds = this.getRefillVariantIds();
      if (!refillIds.size) return ids;
      const ordered = [
        ...ids.filter((id) => refillIds.has(String(id))),
        ...ids.filter((id) => !refillIds.has(String(id))),
      ];
      return ordered;
    },

    buildIdToName() {
      const map = {};
      const add = (id, name) => {
        if (id != null && id !== "" && name) map[String(id)] = name;
      };
      document
        .querySelectorAll("[data-kess-stella-current-variants], [data-kess-stella-refill-variants]")
        .forEach((el) => {
          this.parse(el.textContent, []).forEach((v) => add(v.id, v.option || v.option1));
        });
      document.querySelectorAll("[data-kess-set-product-variants]").forEach((el) => {
        this.parse(el.textContent, []).forEach((v) => add(v.id, v.option1));
      });
      document.querySelectorAll("[data-kess-duo-shade], [data-kess-set-shade]").forEach((button) => {
        add(button.dataset.shadeId, button.dataset.shadeOption);
        add(button.dataset.targetVariantId, button.dataset.shadeOption);
      });
      return map;
    },

    matchSingleControls(ids) {
      const orderedIds = this.prioritizeRefillIds(ids);
      const idToName = this.buildIdToName();
      const radios = Array.from(document.querySelectorAll("variant-selects")).flatMap((selector) => {
        const optionGroups = selector.querySelectorAll(".product-form__input");
        const lastOption = optionGroups[optionGroups.length - 1];
        return lastOption ? Array.from(lastOption.querySelectorAll("input[type='radio']")) : [];
      });
      const matched = [];
      const seen = new Set();
      orderedIds.forEach((id) => {
        const name = idToName[id];
        if (!name) return;
        radios.forEach((radio) => {
          if (seen.has(radio)) return;
          if (this.normalize(radio.value) === this.normalize(name)) {
            seen.add(radio);
            const swatch = document.querySelector(`label[for="${CSS.escape(radio.id)}"] .swatch-color`);
            matched.push({
              el: radio,
              type: "single",
              colorName: radio.value,
              option: radio.value,
              swatchStyle: swatch ? swatch.getAttribute("style") || "" : "",
              image: "",
            });
          }
        });
      });
      return matched;
    },

    matchDuoControls(ids) {
      const orderedIds = this.prioritizeRefillIds(ids);
      const buttons = Array.from(document.querySelectorAll("[data-kess-duo-shade]"));
      if (!buttons.length) return [];
      const idToName = this.buildIdToName();
      const byId = {};
      buttons.forEach((button) => {
        byId[String(button.dataset.shadeId)] = button;
        if (button.dataset.mainVariantId) byId[String(button.dataset.mainVariantId)] = button;
      });
      const matched = [];
      const seen = new Set();
      const push = (button) => {
        if (button && !seen.has(button)) {
          seen.add(button);
          matched.push(this.duoDescriptor(button));
        }
      };
      orderedIds.forEach((id) => {
        const name = idToName[id];
        push(
          byId[id] ||
            buttons.find(
              (button) =>
                name &&
                this.normalize(button.dataset.shadeOption || button.dataset.shadeTitle) === this.normalize(name),
            ),
        );
      });
      return matched;
    },

    matchSetComponents(ids, { limitMatches = true } = {}) {
      // The AI returns refill/source variant ids in the same id-space as the shade buttons'
      // data-shade-id, so match recommendations to a component's shade buttons purely by id
      // (refill ids prioritised first).
      const orderedIds = this.prioritizeRefillIds(ids).map(String);
      const components = Array.from(document.querySelectorAll("[data-kess-set-component]"));
      return components.map((component) => {
        const buttons = Array.from(component.querySelectorAll("[data-kess-set-shade]"));
        const matched = [];
        const seen = new Set();
        const push = (button) => {
          if (button && !seen.has(button)) {
            seen.add(button);
            matched.push(this.duoDescriptor(button));
          }
        };
        orderedIds.forEach((id) => {
          push(
            buttons.find(
              (button) => String(button.dataset.shadeId) === id || String(button.dataset.targetVariantId) === id,
            ),
          );
        });
        const link = component.querySelector(".kess-set-component__image");
        const limitedMatched = limitMatches
          ? matched.slice(0, component.dataset.componentType === "duo" ? 2 : 1)
          : matched;
        const result = {
          component,
          matched: limitedMatched,
          type: component.dataset.componentType,
          title: component.dataset.productName || "",
          image:
            component.dataset.resultImage ||
            component.querySelector(".kess-set-component__image img")?.currentSrc ||
            link?.querySelector("img")?.src ||
            "",
          url: link?.getAttribute("href") || "",
        };
        return result;
      });
    },

    // --- copy --------------------------------------------------------------

    formatNames(names) {
      const unique = [...new Set(names.filter(Boolean))].map((name) =>
        name
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" "),
      );
      if (unique.length <= 1) return unique[0] || "";
      if (unique.length === 2) return unique.join(" & ");
      return unique.slice(0, -1).join(", ") + " & " + unique[unique.length - 1];
    },

    fillTokens(text, tokens) {
      return String(text || "")
        .replace(/\[name\]/g, tokens.name || "")
        .replace(/\[client name\]/gi, tokens.name || "")
        .replace(/\[customer name\]/gi, tokens.name || "")
        .replace(/\[recommended shade\]/g, tokens.shade || "")
        .replace(/\[Product title\]/g, tokens.title || "")
        .replace(/\s{2,}/g, " ")
        .replace(/^[\s,]+/, "")
        .trim();
    },

    // --- rendering ---------------------------------------------------------

    cloneTemplate(name) {
      const modal = this.getModal();
      const selector = `[data-kess-stella-${name}-template]`;
      const template = modal?.querySelector(selector) || document.querySelector(selector);
      return template?.content?.firstElementChild ? template.content.firstElementChild.cloneNode(true) : null;
    },

    mountResult(resultEl) {
      const modal = this.getModal();
      const body = modal?.querySelector("[data-kess-stella-body]");
      if (!body) {
        return;
      }
      const panel = body.querySelector("[data-kess-stella-panel]");
      if (panel) panel.hidden = true;
      body.querySelectorAll("[data-kess-stella-result], .kess-stella-loading").forEach((el) => el.remove());
      body.appendChild(resultEl);
    },

    mountLoading() {
      const loading = this.cloneTemplate("loading");
      if (!loading) {
        return;
      }
      const modal = this.getModal();
      const body = modal?.querySelector("[data-kess-stella-body]");
      if (!body || body.querySelector(".kess-stella-loading")) {
        return;
      }
      const panel = body.querySelector("[data-kess-stella-panel]");
      if (panel) panel.hidden = true;
      body.querySelectorAll("[data-kess-stella-result]").forEach((el) => el.remove());
      body.appendChild(loading);
    },

    restoreFlow() {
      const modal = this.getModal();
      const body = modal?.querySelector("[data-kess-stella-body]");
      if (!body) {
        return;
      }
      body.querySelectorAll("[data-kess-stella-result], .kess-stella-loading").forEach((el) => el.remove());
      const panel = body.querySelector("[data-kess-stella-panel]");
      if (panel) panel.hidden = false;
    },

    buildCard(card) {
      const el = this.cloneTemplate("card");
      if (!el) return null;
      const imageLink = el.querySelector("[data-kess-stella-card-image]");
      const image = imageLink?.querySelector("img");
      if (image && card.image) {
        image.src = card.image;
        image.alt = card.title || "";
      } else if (imageLink) {
        imageLink.remove();
      }
      if (imageLink && card.url) imageLink.setAttribute("href", card.url);
      const title = el.querySelector("[data-kess-stella-card-title]");
      if (title) title.textContent = card.title || "";

      const swatches = el.querySelector("[data-kess-stella-card-swatches]");
      const label = el.querySelector("[data-kess-stella-card-label]");
      if (card.matched && card.matched.length) {
        card.matched.forEach((match) => {
          const swatch = document.createElement("span");
          swatch.className = "kess-stella-card__swatch swatch-color block";
          if (match.swatchStyle) swatch.style.cssText = match.swatchStyle;
          swatches.appendChild(swatch);
        });
        if (label) label.textContent = this.formatNames(card.matched.map((m) => m.colorName));
      } else {
        el.querySelector(".kess-stella-card__shade")?.remove();
      }
      return el;
    },

    /** Build + mount the result page. Returns true when matches were rendered. */
    renderResult() {
      const modal = this.getModal();
      if (!modal) {
        return false;
      }
      const ids = this.getRecommendedIds();
      if (!ids.length) {
        return false;
      }

      const layout = modal.dataset.productType || "single";
      const cards = [];
      const allNames = [];

      if (layout === "set") {
        const components = this.matchSetComponents(ids);
        const matchedComponents = components.filter((c) => c.matched.length);
        if (!matchedComponents.length) {
          return false;
        }
        matchedComponents.forEach((c) => {
          allNames.push(...c.matched.map((m) => m.colorName));
          cards.push({ title: c.title, image: c.image, url: c.url, matched: c.matched });
        });
      } else if (layout === "duo") {
        const matched = this.matchDuoControls(ids).slice(0, 2);
        if (!matched.length) {
          return false;
        }
        allNames.push(...matched.map((m) => m.colorName));
        cards.push({
          title: modal.dataset.productTitle,
          image: modal.dataset.productImage,
          url: modal.dataset.productUrl,
          matched,
        });
      } else {
        // single / mono => one recommended shade
        const matched = (layout === "mono" ? this.matchDuoControls(ids) : this.matchSingleControls(ids)).slice(0, 1);
        if (!matched.length) {
          return false;
        }
        allNames.push(...matched.map((m) => m.colorName));
        cards.push({
          title: modal.dataset.productTitle,
          image: modal.dataset.productImage,
          url: modal.dataset.productUrl,
          matched,
        });
      }

      const result = this.cloneTemplate("result");
      if (!result) {
        return false;
      }

      const tokens = {
        name: this.getFirstName(),
        shade: this.formatNames(allNames),
        title: modal.dataset.productTitle || "",
      };
      const heading = result.querySelector("[data-kess-stella-heading]");
      const subheading = result.querySelector("[data-kess-stella-subheading]");
      if (heading) heading.textContent = this.fillTokens(heading.textContent, tokens);
      if (subheading) subheading.textContent = this.fillTokens(subheading.textContent, tokens);

      const cardsContainer = result.querySelector("[data-kess-stella-cards]");
      cards.forEach((card) => {
        const cardEl = this.buildCard(card);
        if (cardEl) cardsContainer.appendChild(cardEl);
      });

      result.querySelector("[data-kess-stella-select]")?.addEventListener("click", () => {
        this.applyRecommendations().finally(() => this.closeModal());
      });
      result.querySelector("[data-kess-stella-repeat]")?.addEventListener("click", () => this.restartFlow());

      this.mountResult(result);
      return true;
    },

    pollRender(attempt, autoSelect) {
      if (this.renderResult()) {
        if (autoSelect && this.isAnalysisPage()) this.applyRecommendations();
        return;
      }
      if (attempt < 10) {
        setTimeout(() => this.pollRender(attempt + 1, autoSelect), 150);
      } else {
        this.restoreFlow();
      }
    },

    prepareStoredResult() {
      clearInterval(this._watchTimer);
      if (!this.getRecommendedIds().length) return;
      if (this.renderResult()) {
        this.applyMarkers();
        return;
      }
      this.restoreFlow();
    },

    startFlow({ clearStorage = false, forceStart = false } = {}) {
      const modal = this.getModal();
      const body = modal?.querySelector("[data-kess-stella-body]");
      if (!body) {
        return;
      }
      if (clearStorage) this.clearAnalysisData();

      body.querySelectorAll("[data-kess-stella-result], .kess-stella-loading").forEach((el) => el.remove());
      let panel = body.querySelector("[data-kess-stella-panel]");
      if (!panel) {
        panel = document.createElement("div");
        panel.className = "stella-ai-modal__panel";
        panel.setAttribute("data-kess-stella-panel", "");
        body.appendChild(panel);
      }

      panel.innerHTML = "";
      const analysis = document.createElement("stella-analysis");
      analysis.setAttribute("funnel", modal.dataset.funnel || "kess-pdp");
      if (forceStart) analysis.setAttribute("forceStart", "true");
      panel.appendChild(analysis);
      panel.hidden = false;
    },

    restartFlow() {
      this.startFlow({ clearStorage: true, forceStart: true });
    },

    closeModal() {
      const modal = this.getModal();
      if (!modal) {
        return;
      }
      if (typeof modal.hide === "function") {
        modal.hide();
      } else {
        modal.hidden = true;
        modal.removeAttribute("open");
      }
    },

    closeNativePopup() {
      const popup = window.stellaAnalysisPopup || document.querySelector("stella-analysis-popup");
      if (!popup) return;
      if (typeof popup.close === "function") {
        popup.close();
      } else {
        popup.isOpen = false;
      }
    },

    // --- apply selection on the PDP ---------------------------------------

    async applyRecommendations() {
      const modal = this.getModal();
      if (!modal) {
        return;
      }
      const ids = this.getRecommendedIds();
      if (!ids.length) {
        return;
      }
      const layout = modal.dataset.productType || "single";

      if (layout === "single") {
        const matched = this.matchSingleControls(ids).slice(0, 1);
        matched.forEach((match) => {
          match.el.checked = true;
          match.el.dispatchEvent(new Event("change", { bubbles: true }));
        });
        return;
      }

      if (layout === "mono") {
        const matched = this.matchDuoControls(ids).slice(0, 1);
        for (const match of matched) {
          match.el.click();
          await this.sleep(180);
        }
        return;
      }

      if (layout === "duo") {
        const matched = this.matchDuoControls(ids).slice(0, 2);
        const tabs = document.querySelectorAll("[data-kess-duo-tab]");
        if (matched[0]) {
          tabs[0]?.click();
          await this.sleep(60);
          matched[0].el.click();
          await this.sleep(200);
        }
        if (matched[1]) {
          tabs[1]?.click();
          await this.sleep(60);
          matched[1].el.click();
          await this.sleep(220);
        }
        return;
      }

      if (layout === "set") {
        const components = this.matchSetComponents(ids);
        for (const c of components) {
          if (!c.matched.length) continue;
          if (c.type === "duo") {
            const tabs = c.component.querySelectorAll("[data-kess-set-tab]");
            if (c.matched[0]) {
              tabs[0]?.click();
              await this.sleep(60);
              c.matched[0].el.click();
              await this.sleep(220);
            }
            if (c.matched[1]) {
              tabs[1]?.click();
              await this.sleep(60);
              c.matched[1].el.click();
              await this.sleep(220);
            }
          } else {
            c.matched[0].el.click();
            await this.sleep(220);
          }
        }
      }
    },

    // --- "Your Shade" markers (all PDPs) ----------------------------------

    clearMarkers() {
      document.querySelectorAll(".kess-stella-your-shade").forEach((el) => el.remove());
      document
        .querySelectorAll(".kess-stella-has-your-shade")
        .forEach((el) => el.classList.remove("kess-stella-has-your-shade"));
    },

    gatherAllMatches(ids) {
      const layout = this.getModal()?.dataset.productType || "single";
      if (layout === "set") {
        return this.matchSetComponents(ids, { limitMatches: false }).flatMap((component) => component.matched);
      }
      if (layout === "duo" || layout === "mono") return this.matchDuoControls(ids);
      return this.matchSingleControls(ids);
    },

    applyMarkers() {
      this.clearMarkers();
      const ids = this.getRecommendedIds();
      if (!ids.length) {
        return;
      }
      const text = this.getModal()?.dataset.yourShade || "";
      if (!text) {
        return;
      }
      const matches = this.gatherAllMatches(ids);
      matches.forEach((match) => {
        // The recommended swatch is marked with a badge that reuses the existing
        // .kess-duo-shade__badge look ("Your Shade" text). No selected state and
        // no tooltip. The badge sits inside the swatch media (the relatively-
        // positioned host): the <label> for single radios, .kess-duo-shade__media
        // for duo/set shade buttons.
        const host =
          match.type === "single" ? document.querySelector(`label[for="${CSS.escape(match.el.id)}"]`) : match.el;
        if (!host) return;
        const media = host.querySelector(".kess-duo-shade__media") || host;
        if (media.querySelector(".kess-stella-your-shade")) return;
        media.classList.add("kess-stella-has-your-shade");
        const badge = document.createElement("span");
        badge.className = "kess-duo-shade__badge kess-stella-your-shade";
        badge.textContent = text;
        media.appendChild(badge);
      });
    },
  };

  // Stella widget (ticket 446): open the PDP modal and show the custom result.
  window.KessOpenStellaResult = function () {
    Kess.Stella.closeNativePopup();
    const modal = Kess.Stella.getModal();
    if (!modal) return;
    if (!modal.hasAttribute("open")) {
      modal.show?.(null, false);
    }
    if (Kess.Stella.getRecommendedIds().length) {
      if (!Kess.Stella.renderResult()) Kess.Stella.restoreFlow();
    }
  };

  /**
   * ============================================================================
   * INITIALIZATION
   * ============================================================================
   */

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      Kess.Stella.init();
    });
  } else {
    Kess.Stella.init();
  }
})();
